/*!
 * egt.forge.start — Forge project-request intake wizard (app-scoped).
 *
 * The deep agency intake. Renders a single ua.wizard (the platform multi-step
 * control) with 9 logical steps grouping the 14 spec sections
 * (task-tracker/20-FORGE-INTAKE-WIZARD-SPEC.md). The built-in Summary step
 * auto-appends before submit. Email verification gates leaving Step 1 and the
 * Codex consent fires on final submit — both wired in /src/forge-intake.js via
 * the wizard AddHandler API. Submit POSTs to /o/wizard/forge-start
 * (app/routes/wiz.forge.js).
 *
 * House rules: frosted glass over imagery (no scrim), true black, scarce gold,
 * rem only, useNavigation/useFooter:false on the page JSON (G-4).
 *
 * Field names below are the contract shared with wiz.forge.js + forge.risk.js.
 * DO NOT rename without updating both.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.forge.start",
  title: "EGT Forge — Project Request Wizard",
  description: "Multi-step Forge project-request intake (platform wizard + controls).",
  supportServer: true
});

var S = require("../helpers/egt.sections.js");

// ─── option helpers ───────────────────────────────────────────────────────────

function opts(pairs) {
  return pairs.map(function (p) {
    return { value: p[0], label: p[1] };
  });
}

function selectGroup(spec) {
  return {
    n: "ua.select.group",
    name: spec.name,
    title: spec.title,
    required: spec.required === true,
    // ua.select.group requires "inner" (an array of {value, title}) — NOT
    // "options" and NOT {value, label}. This was silently mismatched.
    inner: spec.options.map(function (p) { return { value: p[0], title: p[1] }; })
  };
}

function multiSelect(spec) {
  return {
    n: "ua.select.multiple",
    name: spec.name,
    title: spec.title,
    required: spec.required === true,
    searchable: spec.searchable === true,
    "options-json": JSON.stringify(opts(spec.options))
  };
}

function cappedSelect(spec) {
  return {
    n: "egt.select.capped",
    name: spec.name,
    title: spec.title,
    required: spec.required === true,
    max: spec.max || 3,
    "options-json": JSON.stringify(opts(spec.options))
  };
}

function longText(name, title) {
  return { n: "ua.doc.simple", name: name, title: title, class: "pt-2" };
}

function input(spec) {
  var node = {
    n: "ua.input",
    name: spec.name,
    title: spec.title,
    type: spec.type || "text",
    required: spec.required === true,
    placeholder: spec.placeholder || "",
    icon: spec.icon || undefined,
    class: "pt-2"
  };
  if (spec.minlength) node.minlength = spec.minlength;
  return node;
}

function fieldRow(node) {
  return { n: "div", c: "row egt-forge-wiz__field", i: { n: "div", c: "col-12", i: node } };
}

function step(title, icon, description, fields) {
  return {
    "data-wizard-title": title,
    wizardHeader: { title: title, icon: icon, description: description },
    n: "div",
    c: "egt-forge-wiz__step",
    i: fields.map(fieldRow)
  };
}

// ─── the wizard ───────────────────────────────────────────────────────────────

function buildWizard() {
  var steps = [];

  // STEP 1 — Contact Information (email verified on leaving this step)
  steps.push(step("Let's connect", "diagram-3",
    "First, how we reach you. We'll verify your email before you continue.", [
    input({ name: "fullname", title: "Full name", required: true, minlength: 3, icon: "bi-person", placeholder: "Jordan Avery" }),
    input({ name: "company", title: "Company / organization name", required: true, minlength: 3, icon: "bi-building", placeholder: "Acme Inc." }),
    input({ name: "website", title: "Website", type: "url", icon: "bi-globe", placeholder: "https://example.com" }),
    { n: "ua.checkbox", name: "websiteNone", title: "I don't have one / I need one created." },
    input({ name: "email", title: "Email address", type: "email", required: true, icon: "bi-envelope", placeholder: "you@example.com" }),
    { n: "ua.phone", name: "phone", title: "Phone number", icon: "bi-telephone", required: false, international: false, placeholder: "+16140000009" },
    selectGroup({ name: "contactMethod", title: "Preferred contact method", required: true, options: [
      ["email", "Email"], ["phone", "Phone"], ["text", "Text"], ["video", "Video call"]
    ] }),
    { n: "egt.select.datetimespan", name: "bestTime", title: "Best time to contact you" },
    // HONEYPOT (spam layer): a hidden field humans never see. Bots that fill
    // every input trip it; the submit route rejects any non-empty value. Kept
    // off-screen + aria-hidden + autocomplete-off so assistive tech + password
    // managers skip it. forge-intake.js mirrors its value into the submitted
    // document at submit (step 1 has uae controls, so this raw input is not
    // auto-collected on its own).
    { n: "div", c: "egt-forge-wiz__hp", "aria-hidden": "true",
      style: "position:absolute;left:-9999px;width:.0625rem;height:.0625rem;overflow:hidden;",
      i: { n: "input", type: "text", id: "forge-hp", name: "company_url",
        tabindex: "-1", autocomplete: "off", "aria-hidden": "true" } }
  ]));

  // STEP 2 — Verify email (an INTEGRATED step, not a popup). forge-intake.js
  // sends the code when leaving step 1 and checks it (fail-closed) when leaving
  // this step. The code field is NOT `required` so the session-verified
  // fast-path can pass with it empty; the step handler enforces it otherwise.
  // #forge-verify-email / #forge-verify-status are populated by forge-intake.js.
  steps.push(step("Verify your email", "shield-check",
    "We just emailed you a 6-digit code — enter it to confirm this is you.", [
    { n: "p", c: "egt-forge-wiz__verify-copy",
      i: [ "Enter the code we sent to ", { n: "strong", id: "forge-verify-email", i: "your email" }, "." ] },
    /* NO `maxlength` — it was "6", and that was a real bug, not a nicety.
     *
     * The code email prints the six digits as "123 456" and readers group them
     * as "123-456", so the thing people actually paste is SEVEN characters. A
     * maxlength="6" field clips a paste to fit BEFORE any script sees it, so
     * "123-456" became "123-45" and the reader was told a correctly copied code
     * did not match. Removing the cap is what makes "ignore spaces and dashes"
     * possible here at all. The typed grouping then STAYS in the box (founder
     * ruling 2026-07-24 — do not rewrite the preview); forge-intake.js strips the
     * separators from the copy it POSTs, and the server enforces the real shape
     * (exactly six digits) where it counts. `required` stays false on purpose —
     * see the note above about the session-verified fast path.
     *
     * `autocomplete: "one-time-code"` is load-bearing — it is what lets the OS
     * offer the code from the arriving email. Never narrow it to "off". */
    { n: "ua.input", name: "code", title: "Verification code", type: "text", required: false, summary: "false",
      icon: "bi-shield-check", placeholder: "123 456", class: "pt-2",
      autocomplete: "one-time-code", inputmode: "numeric" },
    { n: "p", id: "forge-verify-status", c: "pt-2 egt-forge-wiz__verify-status", role: "status", "aria-live": "polite", i: "" }
  ]));

  // STEP 3 — Business Information
  steps.push(step("Your business", "briefcase",
    "Tell us who you are and who decides.", [
    longText("companyDoes", "What does your company do?"),
    selectGroup({ name: "inBusiness", title: "How long in business?", required: true, options: [
      ["not-launched", "Not launched yet"], ["under-1yr", "Less than 1 year"], ["1-3yrs", "1–3 years"],
      ["3-5yrs", "3–5 years"], ["5plus", "5+ years"]
    ] }),
    { n: "ua.address", name: "location", title: "Where is your business located?" },
    longText("audience", "Current customer base / target audience?"),
    selectGroup({ name: "decisionMaker", title: "Are you the final decision-maker?", required: true, options: [
      ["yes", "Yes"], ["no", "No"], ["team", "I am part of the decision-making team"]
    ] }),
    { n: "egt.people.group", name: "approvers", title: "If not, who else is involved in approvals?", max: 2,
      description: "Add the other decision-makers (we do not contact them without your okay)." }
  ]));

  // STEP 3 — Project Type + Project Summary
  steps.push(step("What you need", "stars",
    "The services in play and a quick summary.", [
    multiSelect({ name: "services", title: "What services? Select all that apply.", required: true, searchable: true, options: [
      ["marketing-strategy-review", "Marketing strategy & review"],
      ["digital-advertising", "Digital advertising"],
      ["campaign-management", "Start a new campaign & campaign management"],
      ["new-brand", "Create a new brand"],
      ["rebrand", "Rebrand an existing or legacy brand"],
      ["website-design", "Website design"],
      ["event-livestream-production", "Event & live-stream event production"],
      ["commercial-film-production", "Commercial / film production"],
      ["fashion-product-photoshoot", "Fashion / product photoshoot"],
      ["website-analysis", "Website analysis"],
      ["advanced-development", "Advanced development"]
    ] }),
    input({ name: "servicesOther", title: "Other service (optional)", icon: "bi-three-dots" }),
    longText("summary", "Briefly describe what you need help with."),
    longText("problem", "What problem are you trying to solve?"),
    longText("outcome", "What outcome would make this project successful?"),
    selectGroup({ name: "scopeClarity", title: "Do you already have a clear project scope?", required: true, options: [
      ["exact", "Yes, exactly"], ["somewhat", "Somewhat, need guidance"], ["help", "No, need help defining"]
    ] })
  ]));

  // STEP 4 — Goals & Priorities
  steps.push(step("Goals", "bullseye",
    "What success looks like.", [
    cappedSelect({ name: "goals", title: "Primary goals? Select up to 3.", required: true, max: 3, options: [
      ["leads", "Generate leads"], ["sales", "Increase sales"], ["awareness", "Build brand awareness"],
      ["launch", "Launch a new product/service"], ["website-perf", "Improve website performance"],
      ["social", "Improve social media presence"], ["content", "Create professional content"],
      ["investors", "Attract investors/partners"], ["credibility", "Improve credibility"],
      ["retain", "Retain existing customers"]
    ] }),
    input({ name: "goalsOther", title: "Other goal (optional)", icon: "bi-three-dots" }),
    longText("mostImportant", "Most important result?"),
    longText("measureSuccess", "How will you measure success? (leads, sales, traffic, bookings, engagement, inquiries, conversions)")
  ]));

  // STEP 5 — Budget + Timeline
  steps.push(step("Budget & timeline", "cash-coin",
    "Investment and when you want to move.", [
    selectGroup({ name: "budget", title: "Budget range", required: true, options: [
      ["under-10k", "< $10,000"], ["10k-50k", "$10,000–$50,000"], ["50k-100k", "$50,000–$100,000"],
      ["100k-500k", "$100,000–$500,000"], ["500k-plus", "$500,000+"], ["not-sure", "Not sure / need guidance"]
    ] }),
    selectGroup({ name: "budgetApproved", title: "Is this budget approved?", required: true, options: [
      ["yes", "Yes"], ["not-yet", "Not yet"], ["partially", "Partially"], ["proposal-first", "I need a proposal first"]
    ] }),
    selectGroup({ name: "understandsInvestment", title: "Do you understand professional work requires strategy, planning, and proper investment?", required: true, options: [
      ["yes", "Yes"], ["learn-more", "I'd like to learn more"]
    ] }),
    selectGroup({ name: "start", title: "When would you like to start?", required: true, options: [
      ["immediately", "Immediately"], ["2-weeks", "Within 2 weeks"], ["30-days", "Within 30 days"],
      ["60-days", "Within 60 days"], ["3-plus-months", "3+ months"], ["not-sure", "Not sure"]
    ] }),
    selectGroup({ name: "hardDeadline", title: "Is there a hard deadline?", required: true, options: [
      ["yes", "Yes"], ["no", "No"]
    ] }),
    { n: "ua.select.date", name: "deadlineDate", title: "If yes, what is the deadline?" },
    longText("deadlineReason", "If yes, why is that the deadline?"),
    longText("eventDetails", "Tied to an event/launch/campaign/promotion? Provide product details (generalizations are fine for protected IP).")
  ]));

  // STEP 6 — Current Assets & Access + Previous Work
  steps.push(step("Assets & history", "folder",
    "What you already have, and your past experience.", [
    multiSelect({ name: "assets", title: "Current assets & access — select all that apply.", searchable: true, options: [
      ["brand-kit", "Brand kit / guidelines"], ["vector-files", "Vector files"], ["font-package", "Font package"],
      ["website-access", "Website & dashboard access"], ["domain", "Domain name"],
      ["social", "Business social media account"], ["analytics", "Google Analytics"],
      ["ad-accounts", "Ad accounts"], ["email-platform", "Email marketing platform"],
      ["media-assets", "Photography / video assets"], ["copy", "Copy / written content"],
      ["customer-data", "Customer data / mailing list"], ["none", "None yet"]
    ] }),
    selectGroup({ name: "canProvideAccess", title: "Can you provide access to required platforms/accounts if needed?", required: true, options: [
      ["yes", "Yes"], ["no", "No"], ["help-setup", "I need help with setup"]
    ] }),
    longText("toolsList", "List tools/platforms/accounts currently used (Shopify, WordPress, Squarespace, Webflow, Meta Ads, Google Ads, Mailchimp, HubSpot…)"),
    multiSelect({ name: "workedWith", title: "Worked with an agency/freelancer/production team before?", options: [
      ["agency", "Agency"], ["freelancer", "Freelancer"], ["production-team", "Production team"], ["none", "None"]
    ] }),
    longText("prevWork", "If yes, what worked well and what did not?"),
    selectGroup({ name: "availability", title: "Will you + the decision-maker be available for planning, preproduction, pre-campaign, and review meetings?", required: true, options: [
      ["yes", "Yes"], ["hands-off", "We prefer a more hands-off approach"]
    ] }),
    longText("inspirations", "Examples of work you like / inspirations? Include links."),
    longText("competitors", "Top competitors or similar brands? (Or say you'd like help here.)"),
    longText("avoid", "Styles/formats/deliverables to avoid?")
  ]));

  // STEP 7 — Decision & Approval + Payment & Professional Fit
  steps.push(step("Decisions & fit", "people",
    "How decisions get made and how you like to work with a partner.", [
    // P1-2 DEVIATION (accepted): the spec wants §10 "who approves" as a dropdown
    // sourced from the §2 decision-maker list + "I will". Wiring a select to a
    // PRIOR step's egt.people.group state requires a new dependent/cross-step
    // control the platform does not provide (per app/agents.md, ASK before a new
    // shared control; this is not worth one). Kept as guided free-text — the team
    // gets the named approver; the structured link to §2 is the only loss. Noted
    // for a future dependent-select control if it earns its keep.
    longText("approverFinal", "Who will approve final scope and budget? (Name one of your decision-makers from the previous step, or write 'I will'.)"),
    selectGroup({ name: "feedbackPeople", title: "How many people will provide feedback?", required: true, options: [
      ["1", "1"], ["2-3", "2–3"], ["4-5", "4–5"], ["5-8", "5–8"], ["8plus", "8+"]
    ] }),
    selectGroup({ name: "feedbackSpeed", title: "How quickly can your team provide feedback?", required: true, options: [
      ["24h", "Within 24 hours"], ["2-3-days", "2–3 business days"], ["1-week", "1 week"],
      ["1-month", "1 month"], ["quarterly", "Quarterly"], ["unsure", "I don't know if I can commit"]
    ] }),
    selectGroup({ name: "understandsDelays", title: "Do you understand delayed feedback / scope changes / missing materials may affect timeline and cost?", required: true, options: [
      ["yes", "Yes"]
    ] }),
    selectGroup({ name: "deposit", title: "Comfortable with a deposit / upfront payment to begin?", required: true, options: [
      ["yes", "Yes"], ["no", "No"]
    ] }),
    selectGroup({ name: "signAgreement", title: "Comfortable signing a written agreement before work begins? (Familiar with DocuSign / Adobe Sign?)", required: true, options: [
      ["yes", "Yes"], ["no", "No"]
    ] }),
    selectGroup({ name: "milestone", title: "Comfortable with milestone-based payments for larger projects?", required: true, options: [
      ["yes", "Yes"], ["no", "No"], ["budget-whole", "We prefer to budget the whole project in advance"],
      ["pay-at-completion", "We prefer to pay at completion"]
    ] }),
    selectGroup({ name: "currentNeed", title: "Which best describes your current need?", required: true, options: [
      ["ready", "I am ready to hire"], ["comparing", "Comparing vendors"], ["pricing", "Gathering pricing"],
      ["strategy-first", "Need strategy before deciding"], ["not-ready", "Not ready yet"]
    ] })
  ]));

  // STEP 8 — Fraud & Verification
  steps.push(step("Verify your business", "shield-check",
    "To protect both parties, we may verify business details before beginning work.", [
    input({ name: "businessEmail", title: "Business email address", type: "email", icon: "bi-envelope", placeholder: "name@yourcompany.com" }),
    input({ name: "businessWebsite", title: "Business website or public profile", type: "url", icon: "bi-globe" }),
    longText("businessAddress", "Business mailing address"),
    { n: "egt.tin.duns", name: "tinduns", title: "Business TIN or DUNS" },
    selectGroup({ name: "paymentsElectronic", title: "Agree all payments must be electronic / online portal / ACH?", required: true, options: [
      ["yes", "Yes"]
    ] }),
    selectGroup({ name: "workAfterSigned", title: "Agree project work begins only after a signed agreement and cleared deposit?", required: true, options: [
      ["yes", "Yes"]
    ] })
  ]));

  // STEP 9 — Additional Notes
  steps.push(step("Anything else", "chat-left-text",
    "The last word is yours.", [
    longText("notes", "Anything else we should know before reviewing?")
  ]));

  return {
    n: "ua.wizard",
    name: "/o/wizard/forge-start",
    id: "forge-intake-wizard",
    showStepNavigation: true,
    allowStepJump: false,
    summary: true,
    showProgressBar: true,
    progressbarclass: "egt-forge-wiz__bar",
    continueText: "Continue",
    submitText: "Submit project request",
    successTitle: "Your project request is in.",
    successDescription: "Thank you — our team will review your request and reach out. Here is exactly what you sent.",
    successIcon: "send-check",
    successShowAppResponse: false,
    successShowResponse: false,
    inner: steps
  };
}

/* The wizard definition, for the server: wiz.forge.js reads each field's title,
 * control and option labels from here so the team email reads exactly like the
 * on-page summary (one definition, no second copy of the labels). */
exports.buildWizard = buildWizard;

exports.render = async function render() {
  return S.page("/forge/start", [
    renderHero(),
    renderWizard(),
    /* JSON-LD: WebPage + BreadcrumbList (SEO wave 2B-7) */
    S.pageSchema({ path: "/forge/start", name: "Start a Project — EGT Forge", crumbs: [ { name: "Forge", path: "/forge" }, { name: "Start a Project" } ] })
  ]);
};

/* HERO (WP-N4) — an encouraging, approachable band ABOVE the long intake.
 * The wizard is deep on purpose; the hero lowers the activation barrier and
 * reassures fence-sitters before they start. Frosted dark glass over the
 * UNDIMMED Forge canvas (house rule — contrast from the glass only). Honest
 * posture: it's a real conversation with the studio, no dates, no invented
 * features (there is no save/resume, so we never promise one). Both the CTA
 * and the foot cue glide down to #forge-wizard so the form is never lost. */
function renderHero() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-forge-hero",
    i: [
      S.media({ img: "/imgs/egt-forge-canvas.jpg", alt: "", parallax: 0.08, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-forge-hero__inner",
        i: S.glass({ width: "wide", tone: "dark", c: "egt-forge-hero__card egt-reveal",
          content: [
            { n: "p", c: "egt__eyebrow", i: "EGT Forge — Start a project" },
            { n: "h1", c: "egt-forge-hero__title",
              i: [ "Tell us what you're building. ", { n: "br" },
                { n: "span", c: "egt__gold", i: "We'll take it from there." } ] },
            { n: "p", c: "egt-forge-hero__lead",
              i: "This is a deeper intake than a contact form — and that's on purpose. The more we understand the work up front, the better the plan we bring back. Take it a step at a time; there are no wrong answers, and anything you're unsure of, we'll shape together." },
            { n: "ul", c: "egt-forge-hero__chips",
              i: [
                chip("A real conversation", "Your request goes to the studio — real people who read every answer, not an auto-reply."),
                chip("You set the pace", "Nine short steps, grouped and guided. Tell us what you can now; we fill the gaps together."),
                chip("A real plan back", "Once you submit, our team reviews everything and reaches out to scope the work with you.")
              ] },
            { n: "div", c: "egt-forge-hero__actions",
              i: { n: "a", href: "#forge-wizard", c: "egt-cta egt-forge-hero__cta",
                i: [ "Start my project", { n: "span", c: "egt-cta__tick", i: "›" } ] } },
            { n: "p", c: "egt-forge-hero__note",
              i: "Set aside a few minutes · we verify your email once, to keep the conversation secure · no payment needed to send a request." }
          ] }) },
      /* foot scroll cue — a real anchor to the wizard, with a gently bobbing
       * chevron. Native jump is the fallback + the reduced-motion behavior. */
      { n: "a", href: "#forge-wizard", c: "egt-forge-hero__scroll",
        "aria-label": "Scroll to the project request form",
        i: [
          { n: "span", c: "egt-forge-hero__scroll-t", i: "Begin" },
          { n: "span", c: "egt-forge-hero__scroll-ch", "aria-hidden": "true" }
        ] }
    ]
  };
}

function chip(k, v) {
  return {
    n: "li", c: "egt-forge-hero__chip",
    i: [
      { n: "span", c: "egt-forge-hero__chip-k", i: k },
      { n: "span", c: "egt-forge-hero__chip-v", i: v }
    ]
  };
}

/* THE WIZARD — the deep intake, kept fully intact, on its own canvas below the
 * hero. id="forge-wizard" is the scroll target for the hero CTA + cue. */
function renderWizard() {
  var card = S.glass({
    width: "wide", tone: "dark", c: "egt-forge-wiz__card egt-reveal",
    content: [
      { n: "div", id: "forge-wiz-status", c: "egt-forge-wiz__status d-none", role: "status", "aria-live": "polite", tabindex: "-1", i: "" },
      buildWizard()
    ]
  });

  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-forge-wiz", id: "forge-wizard",
    i: [
      S.media({ img: "/imgs/egt-about-canvas.jpg", alt: "", parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-forge-wiz__inner",
        i: [ card ] }
    ]
  };
}
